import { toast } from '@/lib/hooks/use-toast'
import { queryClient } from '@/lib/query-client'
import { getRoomNameForCreate } from '@/lib/room-create'
import { trackEvent } from '@/lib/telemetry'
import { deriveErrorMessage } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { z } from 'zod'
import { executeDataverseChangeset } from './dataverse-changeset'
import {
  createRecord,
  getGlobalOptionSetDefinition,
  getMultipleRecords,
  GlobalOptionSetDefinition,
} from './helpers'
import { MissingRequiredDataError, type MissingDataRecord } from './missing-required-data-error'
import { QUERY_KEYS } from './query-keys'
import type { QuoteDetailData } from './quote-details'
import { useQuoteQuery } from './quotes'
import {
  applyRoomMovePlan,
  buildRoomMoveOperations,
  formatComposedRoomLabel,
  formatRoomLabel,
  getChildRooms,
  sortRoomsHierarchically,
  type RoomMovePlan,
} from './room-hierarchy'
import { getOtherScopeTagValue } from './scope-tag-buckets'

export const accountRoomSchema = z.object({
  dsy_accountroomid: z.string({ message: 'Room ID is missing.' }),
  dsy_name: z.string({
    message: 'Room name is required. Please set a room name on the Dynamics account room record.',
  }),
  dsy_sequencenumber: z.number().nullable(),
  dsy_roomnumber: z.string().nullable(),
  _dsy_parentroom_value: z.string().nullable().default(null),
})

export type AccountRoomData = z.infer<typeof accountRoomSchema>

export function useAccountRoomsQuery() {
  const { data: quote } = useQuoteQuery()
  const accountId = quote?._customerid_value
  return useQuery({
    queryKey: QUERY_KEYS.accountRooms,
    enabled: !!accountId,
    queryFn: async () => {
      if (!accountId) {
        throw new Error('Related account not found on the quote.')
      }
      const rooms = await getMultipleRecords(
        'dsy_accountrooms',
        `?$select=dsy_accountroomid,dsy_name,dsy_sequencenumber,dsy_roomnumber,_dsy_parentroom_value&$filter=_dsy_accountid_value eq '${accountId}'`
      )
      const parsed: AccountRoomData[] = []
      const failures: MissingDataRecord[] = []
      for (const room of rooms) {
        const result = accountRoomSchema.safeParse(room)
        if (result.success) {
          parsed.push(result.data)
        } else {
          const raw = room as Record<string, unknown>
          failures.push({
            id: typeof raw.dsy_accountroomid === 'string' ? raw.dsy_accountroomid : null,
            raw,
            issues: result.error.issues,
          })
        }
      }
      if (failures.length > 0) {
        throw new MissingRequiredDataError('dsy_accountrooms', failures)
      }
      return parsed
    },
    // Module reference, not an inline arrow: react-query reuses a memoised
    // select result only while `options.select` is referentially stable, and
    // every summary call site mounts an observer on this query.
    select: sortRoomsHierarchically,
  })
}

// Room hierarchy (2 levels: parent + child) — pure logic lives in
// room-hierarchy.ts; re-exported here as the room-model entry point.
export {
  applyRoomMovePlan,
  buildFlatRoomNodes,
  buildRoomDropSlots,
  formatRoomLabel,
  buildRoomMoveOperations,
  buildRoomMovePlan,
  buildRoomMoveUndoPlan,
  buildRoomRankParentIdMap,
  buildSiblingReorderPlan,
  canNestRoomInto,
  describeRoomDropPlacement,
  describeRoomMove,
  findCurrentRoomDropSlot,
  getNextRoomDropSlot,
  getRoomDropProjection,
  getRoomChildCount,
  getRoomSelectionState,
  isPointerWithinRoomList,
  isRoomMovePlanApplied,
  removeRoomChildrenOf,
  resolveRoomDropSlot,
  resolveRoomDropZone,
  ROOM_DROP_EDGE_RATIO,
  ROOM_DROP_LIST_X_TOLERANCE,
  toggleAllRoomsSelected,
  getChildRooms,
  getParentRoomId,
  getRoomGroupIds,
  sortRoomsHierarchically,
  type FlatRoomNode,
  type RoomDropProjection,
  type RoomDropSlot,
  type RoomDropSlotKey,
  type RoomDropZone,
  type RoomListBounds,
  type RoomMovePlan,
  type RoomSequenceUpdate,
} from './room-hierarchy'

/**
 * Whether `roomId` is a parent room. The single derivation of
 * `includeChildRooms`, and — since nesting is a sidebar-only concept — the
 * sidebar's alone: it is what makes a parent row show the room-group rollup.
 * Never stored, so a re-parent flips it on the next rooms read.
 */
export function useIsParentRoom(roomId?: string | null) {
  const { data: rooms } = useAccountRoomsQuery()
  return useMemo(() => !!roomId && getChildRooms(roomId, rooms ?? []).length > 0, [roomId, rooms])
}

/**
 * THE room label. Nesting is a sidebar-only concept: everywhere else a child
 * room is just a room whose display name carries its parent — "Floor 1 | 101 -
 * Bedroom 1" — rendered flat in DFS order. One label, no variants, so every
 * consumer
 * (sections, pickers, compact rows, proposal, CSV, AI context, change diffs)
 * prints the same string.
 *
 * Top-level rooms are unchanged. Sidebar rows call {@link getRoomOwnLabel}.
 */
export function getRoomLabel(roomId?: string | null) {
  const byId = readCachedRoomsById()
  const room = roomId ? byId?.get(roomId) : undefined
  if (!room) return undefined
  const parentId = room._dsy_parentroom_value
  return formatComposedRoomLabel(room, parentId ? byId?.get(parentId) : null)
}

/**
 * {@link getRoomLabel} split for RENDERING: `parent` is the parent's name (the
 * context, null at top level), `own` is the room's own full label (the
 * identity). Recomposing them — `parent ? `${parent} | ${own}` : own` — returns
 * `getRoomLabel` byte for byte, which a test locks: this exists so a surface can
 * grey the prefix, never so it can invent a second label rule. The separator
 * glyph and the string form stay exactly as `formatComposedRoomLabel` writes
 * them.
 *
 * Reads the same rooms cache as `getRoomLabel` rather than taking a rooms array,
 * so the two can never be handed different data and drift.
 */
export function getRoomLabelParts(roomId?: string | null): { parent: string | null; own: string } | undefined {
  const byId = readCachedRoomsById()
  const room = roomId ? byId?.get(roomId) : undefined
  const own = formatRoomLabel(room)
  if (!own) return undefined
  const parentId = room?._dsy_parentroom_value
  const parentName = parentId ? byId?.get(parentId)?.dsy_name : null
  return { parent: parentName || null, own }
}

/**
 * The room's OWN label, with no parent prefix — for the sidebar tree, the one
 * surface that renders the hierarchy itself, so the parent is already the row
 * above. Every other surface wants {@link getRoomLabel}.
 *
 * When the row object is already in hand, call `formatRoomLabel(room)` instead —
 * this exists for the id-only call sites.
 */
export function getRoomOwnLabel(roomId?: string | null) {
  const room = roomId ? readCachedRoomsById()?.get(roomId) : undefined
  return formatRoomLabel(room)
}

// Both label lookups run in hot paths — a table sort comparator
// (quote-details-grid-view-dialog) and once per line in the CSV export — so the
// cache array is indexed once and the index reused until react-query hands back
// a new array. Keyed on the array itself, so a rooms refetch rebuilds it and a
// stale index can never outlive its data.
const roomsByIdCache = new WeakMap<AccountRoomData[], Map<string, AccountRoomData>>()

function readCachedRoomsById() {
  const rooms = queryClient.getQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms)
  if (!rooms) return undefined
  const cached = roomsByIdCache.get(rooms)
  if (cached) return cached
  const byId = new Map(rooms.map((room) => [room.dsy_accountroomid, room]))
  roomsByIdCache.set(rooms, byId)
  return byId
}

/** Global max sequence number — new rooms and re-parented rooms land after it. */
export function getMaxRoomSequenceNumber(rooms: AccountRoomData[] | undefined | null) {
  return Math.max(0, ...(rooms ?? []).map((room) => room.dsy_sequencenumber ?? 0))
}

function readCachedRooms() {
  return queryClient.getQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms)
}

/**
 * Create a room, optionally inside a parent. Sequence is the global `max + 1`
 * (unchanged from the flat model — sibling order is the relative order of global
 * values). The optimistic row carries the parent so the tree renders it in place
 * immediately; its `temp-` id gates the actions that need a real record.
 *
 * Callers keep their own UI bookkeeping in per-call `onError`/`onSettled`.
 */
export function useCreateAccountRoom() {
  const queryClient = useQueryClient()
  const { data: quote } = useQuoteQuery()
  const accountId = quote?._customerid_value

  return useMutation({
    // `sequenceNumber` is supplied by the caller so the optimistic row and the
    // persisted record carry the SAME value. Computing it in `mutationFn` reads
    // the cache *after* `onMutate` already wrote the optimistic room into it, so
    // the server would get max + 2 while the UI showed max + 1.
    mutationFn: async ({
      name,
      parentRoomId,
      sequenceNumber,
    }: {
      name: string
      parentRoomId?: string | null
      sequenceNumber?: number
    }) => {
      if (!accountId) {
        throw new Error('Related account not found on the quote.')
      }
      const roomName = getRoomNameForCreate(name)
      if (!roomName) {
        throw new Error('Room name is required')
      }
      return await createRecord('dsy_accountrooms', {
        dsy_name: roomName,
        dsy_sequencenumber: sequenceNumber ?? getMaxRoomSequenceNumber(readCachedRooms()) + 1,
        'dsy_accountid@odata.bind': `/accounts(${accountId})`,
        ...(parentRoomId ? { 'dsy_parentroom@odata.bind': `/dsy_accountrooms(${parentRoomId})` } : {}),
      })
    },
    onMutate: async ({ name, parentRoomId, sequenceNumber }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.accountRooms })
      const previousRooms = queryClient.getQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms)

      const newRoom: AccountRoomData = {
        // Random, not `Date.now()`: two creates in the same millisecond would
        // share an id, giving duplicate React keys and making one `onError`
        // remove both optimistic rows.
        dsy_accountroomid: `temp-${crypto.randomUUID()}`,
        dsy_name: getRoomNameForCreate(name) ?? name,
        dsy_sequencenumber: sequenceNumber ?? getMaxRoomSequenceNumber(previousRooms) + 1,
        dsy_roomnumber: null,
        _dsy_parentroom_value: parentRoomId ?? null,
      }

      queryClient.setQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms, (old) => old?.concat(newRoom))

      return { previousRooms, roomId: newRoom.dsy_accountroomid }
    },
    onError: (error, _, context) => {
      queryClient.setQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms, (old) =>
        old && context?.roomId ? old.filter((room) => room.dsy_accountroomid !== context.roomId) : context?.previousRooms
      )
      toast({ title: 'Error creating room', error, variant: 'destructive' })
    },
    onSuccess: (newRoomId, _variables, context) => {
      if (!context?.roomId) return

      // Swap the temp id for the real one in place. Waiting for a refetch to
      // replace the row costs a full list GET before the room becomes usable.
      queryClient.setQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms, (old) =>
        old?.map((room) =>
          room.dsy_accountroomid === context.roomId ? { ...room, dsy_accountroomid: newRoomId } : room
        )
      )
      // A brand new room provably has zero items — seed the cache instead of
      // making the room view issue a GET that can only come back empty.
      queryClient.setQueryData<QuoteDetailData[]>(QUERY_KEYS.quoteDetails({ roomId: newRoomId }), [])
      queryClient.removeQueries({ queryKey: QUERY_KEYS.quoteDetails({ roomId: context.roomId }) })
    },
    onSettled: () => {
      // The optimistic row already matches what was written, so mark stale
      // without refetching: a refetch here cascades the whole list again, and
      // one landing after a second create's `onMutate` wipes its optimistic row.
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountRooms, refetchType: 'none' })
    },
  })
}

/**
 * Execute one drag-to-reparent plan: bind (or clear) `dsy_parentroom` on the
 * moved room and renumber the siblings the insertion displaced. The nav property
 * for `@odata.bind` is the lowercase `dsy_parentroom`; `null` clears the lookup.
 *
 * **One atomic changeset**, built by `buildRoomMoveOperations`. Every write
 * commits or rolls back together, so there are no intermediate states to be
 * caught in and no staging write to be stranded by a reload — the failure mode
 * the sequential park-then-redeal fan-out had, where a disconnect mid-chain left
 * the moved room parked above the max and it silently ranked last in its group on
 * every quote of the account.
 *
 * One mutation per gesture, never a move followed by a reorder: two hooks would
 * take competing `previousRooms` snapshots and race their rollbacks. `scope`
 * serialises the mutation with itself for the same reason — two drags in flight
 * would otherwise interleave their changesets and their cache rollbacks.
 *
 * Account-wide, like rename and reorder — the drop is the confirmation, and the
 * toast carries Undo.
 */
export function useMoveAccountRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    // Serialised, not parallel: a second drag released mid-flight must apply on
    // top of the first, not race it.
    scope: { id: 'account-room-move' },
    mutationFn: async ({ plan }: { plan: RoomMovePlan; undo?: boolean }) => {
      await executeDataverseChangeset(buildRoomMoveOperations(plan))
    },
    onMutate: async ({ plan }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.accountRooms })
      const previousRooms = queryClient.getQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms)

      queryClient.setQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms, (old) =>
        old ? applyRoomMovePlan(old, plan) : old
      )

      return { previousRooms }
    },
    onError: (error, _, context) => {
      queryClient.setQueryData<AccountRoomData[]>(QUERY_KEYS.accountRooms, context?.previousRooms)
      toast({
        title: 'Error moving room',
        // Atomic: a failed changeset wrote nothing, so the room did not move.
        description: `${deriveErrorMessage(error)} The room was not moved.`,
        variant: 'destructive',
      })
    },
    onSuccess: (_, { plan, undo }) => {
      trackEvent('room-reparent', {
        target: plan.parentRoomId ? 'room' : 'top-level',
        position: String(plan.targetIndex),
        siblingWrites: String(plan.siblingUpdates.length),
        ...(undo ? { undo: 'true' } : {}),
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountRooms })
    },
  })
}

export function useSystemsQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.systems,
    queryFn: () => getGlobalOptionSetDefinition('dsy_ecosystem'),
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export function getSystemLabel(systemId?: number | null) {
  const systems = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.systems)
  if (!systems) return
  return systems.Options.find((system) => system.Value === systemId)?.Label.UserLocalizedLabel.Label
}

export function useSystemScopeTagsQuery(systemId?: number | null) {
  const { data: systems } = useSystemsQuery()

  return useQuery({
    queryKey: QUERY_KEYS.systemScopeTags,
    queryFn: () => getGlobalOptionSetDefinition('dsy_systemscopetags'),
    select: (data) => {
      if (!systemId || !systems) return data

      const systemLabel = systems.Options.find((system) => system.Value === systemId)?.Label.UserLocalizedLabel.Label
      if (!systemLabel) return data

      return {
        ...data,
        Options: data.Options.filter((option) =>
          scopeTagLabelBelongsToSystem(option.Label.UserLocalizedLabel.Label, systemLabel)
        ),
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export function getSystemScopeTagLabel(systemScopeTagId?: number | null) {
  if (!systemScopeTagId) return

  const systemScopeTags = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.systemScopeTags)
  if (!systemScopeTags) return

  return systemScopeTags.Options.find((option) => option.Value === systemScopeTagId)
    ?.Label.UserLocalizedLabel.Label.split(' ')
    .at(1)
}

export {
  getEffectiveScopeTag,
  getOtherScopeTagValue,
  indexDetailsByGlobalId,
  matchesScopeTagBucket,
} from './scope-tag-buckets'

// A scope tag belongs to exactly one system: its label leads with the system
// label (e.g. "Audio Speaker" → Audio). Single owner of that convention.
function scopeTagLabelBelongsToSystem(tagLabel: string, systemLabel: string) {
  return tagLabel.split(' ')[0].toLowerCase() === systemLabel.toLowerCase()
}

// When a desired tag conflicts with the target system — e.g. a price-list
// scope tag from another system — the system wins: fall back to its Other scope.
export function resolveScopeTagForSystem(
  systemId: number | null | undefined,
  scopeTag: number | null | undefined
): number | null {
  if (scopeTag == null || systemId == null) return scopeTag ?? null
  const systemLabel = getSystemLabel(systemId)
  const systemScopeTags = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.systemScopeTags)
  const tagLabel = systemScopeTags?.Options.find((option) => option.Value === scopeTag)?.Label.UserLocalizedLabel.Label
  // Metadata not loaded yet — can't validate, keep the requested tag.
  if (!systemLabel || !tagLabel) return scopeTag
  if (scopeTagLabelBelongsToSystem(tagLabel, systemLabel)) return scopeTag
  return getOtherScopeTagValueForSystem(systemId)
}

export function getOtherScopeTagValueForSystem(systemId?: number | null): number | null {
  if (!systemId) return null
  const systems = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.systems)
  const systemScopeTags = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.systemScopeTags)
  if (!systems || !systemScopeTags) return null
  const systemLabel = systems.Options.find((s) => s.Value === systemId)?.Label.UserLocalizedLabel.Label
  if (!systemLabel) return null
  return getOtherScopeTagValue({
    ...systemScopeTags,
    Options: systemScopeTags.Options.filter((option) =>
      scopeTagLabelBelongsToSystem(option.Label.UserLocalizedLabel.Label, systemLabel)
    ),
  })
}

export function usePhasesQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.phases,
    queryFn: () => getGlobalOptionSetDefinition('dsy_phase'),
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export function getPhaseLabel(phaseId?: number | null) {
  const phases = queryClient.getQueryData<GlobalOptionSetDefinition>(QUERY_KEYS.phases)
  if (!phases) return
  return phases.Options.find((phase) => phase.Value === phaseId)?.Label.UserLocalizedLabel.Label
}
